import { useInitTracking } from 'hooks/useMetrics';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import type { FlowTypes } from 'store/constants';
import { getWidgetLocation } from 'utils';
import { normalizePathname } from 'utils/normalizeUrl';

import { flowAdapter } from './flowAdapter';

interface UseFlowInitParams {
  flow: FlowTypes;
  payGroupId?: number;
  skipRedirectMethod?: boolean;
  userAgent: string;
  appChannel: string;
  isOnRampFlow?: boolean;
  hasOnRampCurrency?: boolean;
}

export const useFlowInit = ({
  flow,
  payGroupId,
  skipRedirectMethod,
  userAgent,
  appChannel,
  isOnRampFlow = false,
  hasOnRampCurrency = false,
}: UseFlowInitParams) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const adapter = flowAdapter[flow];
  const { isMainPage } = getWidgetLocation(normalizePathname(location.pathname));

  const { useInitQuery, useLazyInitQuery, formatter, invalidate, tracking, emptyData } = adapter;

  const {
    data: initData,
    isLoading,
    isFetching: isInitFetching,
    error,
    isSuccess,
    refetch,
    startedTimeStamp,
    fulfilledTimeStamp,
  } = useInitQuery(undefined, {
    skip: !isMainPage,
  });

  useInitTracking({
    isLoading,
    isFetching: isInitFetching,
    isSuccess,
    startedTimeStamp,
    fulfilledTimeStamp,
    metricUrl: tracking?.metricUrl,
    metricName: tracking?.metricName,
    dispatch,
  });

  const [getInitByCurrency, { data: initByCurrencyData, isFetching, originalArgs }] = useLazyInitQuery();

  const refetchInit = (currency?: string) => {
    getInitByCurrency(currency);
  };

  const invalidateFlowCache = useCallback(() => {
    dispatch(invalidate());
  }, [dispatch, invalidate]);

  // Initial formatted data
  const initialData = useMemo(
    () =>
      formatter(
        initData,
        {
          payGroupId,
          skipRedirectMethod,
          isOnRamp: isOnRampFlow,
          hasOnRampCurrency,
        },
        userAgent,
        appChannel,
      ),
    [initData, formatter, payGroupId, skipRedirectMethod, userAgent, isOnRampFlow, hasOnRampCurrency, appChannel],
  );

  const data = useMemo(() => {
    const raw = initByCurrencyData || initData;
    return formatter(
      raw,
      {
        payGroupId,
        skipRedirectMethod,
        isOnRamp: isOnRampFlow,
        hasOnRampCurrency,
      },
      userAgent,
      appChannel,
      originalArgs as string | undefined,
    );
  }, [
    initByCurrencyData,
    initData,
    formatter,
    payGroupId,
    hasOnRampCurrency,
    isOnRampFlow,
    skipRedirectMethod,
    userAgent,
    appChannel,
  ]);

  return {
    initialData,
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    getInitByCurrency,
    formatter,
    invalidateFlowCache,
    refetchInit,
    emptyData,
    initDuration: startedTimeStamp && fulfilledTimeStamp ? fulfilledTimeStamp - startedTimeStamp : undefined,
    hasInitRequest: Boolean(startedTimeStamp),
  };
};
